import React from "react";
import { Link } from "react-router-dom";

const SECTIONS = [
  {
    h: "1. Acceptance of Terms",
    p: `By creating an account or using Fount Stories ("Fount", "we", "us", or "our"), you agree to these Terms of Service ("Terms"). If you do not agree, do not use the service. These Terms form a binding agreement between you and Fount Stories.`,
  },
  {
    h: "2. Eligibility",
    p: `You must be at least 16 years old and able to enter into a legally binding agreement under the laws of your jurisdiction. By using Fount you represent that you meet these requirements and that the information you provide is accurate.`,
  },
  {
    h: "3. Your Account",
    p: `You are responsible for safeguarding your password and for all activity that occurs under your account. Notify us immediately at brief@fountstories.com if you suspect unauthorized access. We are not liable for losses caused by failure to maintain account security.`,
  },
  {
    h: "4. Acceptable Use",
    p: `You agree not to misuse Fount or its outputs. You will not (a) violate any law or third-party right, (b) attempt to reverse engineer, scrape, or interfere with our service, (c) use Fount to create harassing, defamatory, infringing, or illegal content, (d) resell or sublicense access without our written permission, or (e) use Fount in any way that imposes an unreasonable load on our infrastructure.`,
  },
  {
    h: "5. AI-Generated Content",
    p: `Fount uses third-party large language models (currently Anthropic's Claude) to generate talking points, hooks, titles, and editorial calendars based on public news sources. AI outputs may contain inaccuracies, omissions, or biases. You are responsible for reviewing, fact-checking, and editing any AI-generated content before publishing or distributing it. Fount makes no warranty as to the accuracy, completeness, or fitness for purpose of generated content.`,
  },
  {
    h: "6. Third-Party Sources",
    p: `Fount aggregates publicly available RSS feeds and news headlines from third-party publishers. We do not endorse, control, or guarantee the accuracy of third-party content. Linking to or summarizing third-party sources does not imply any affiliation. All trademarks and copyrights of third-party sources remain the property of their respective owners.`,
  },
  {
    h: "7. Subscriptions and Billing",
    p: `Paid plans, when offered, are billed in advance through Stripe. By subscribing you authorize us to charge the payment method you provide. Plans automatically renew at the end of each billing period unless cancelled before the renewal date. We may change pricing on at least thirty (30) days' notice; changes take effect on your next billing cycle. During preview/early-access periods Fount may be offered free of charge.`,
  },
  {
    h: "8. Cancellation and Refunds",
    p: `You may cancel your subscription at any time from your account settings. Cancellation is scheduled at the end of your current billing period — you retain full paid access until that date and then automatically move to the Free plan. New subscribers are covered by a 7-day satisfaction guarantee on their first paid subscription. Annual plans qualify for prorated refunds of unused months when cancelled within 30 days of purchase. Full details, including how to request a refund, chargeback policy, and duplicate-charge handling, are on our Refund & Cancellation Policy at /refund-policy.`,
  },
  {
    h: "9. Intellectual Property",
    p: `Fount, including its software, design, branding, and original content, is owned by Fount Stories and protected by intellectual property laws. We grant you a limited, non-exclusive, non-transferable license to use the service for your personal or professional content creation. Content you generate using Fount, including your saved packets and calendars, belongs to you — you grant Fount a limited license to process and store that content solely to provide the service.`,
  },
  {
    h: "10. Termination",
    p: `We may suspend or terminate your access if you breach these Terms, abuse the service, or expose Fount or other users to legal or operational risk. On termination, your right to use Fount ends immediately; sections that by their nature should survive (including payment, IP, disclaimers, and limitations of liability) will continue to apply.`,
  },
  {
    h: "11. Disclaimers",
    p: `Fount is provided "as is" and "as available" without warranties of any kind, express or implied, including merchantability, fitness for a particular purpose, accuracy, and non-infringement. We do not warrant that the service will be uninterrupted, error-free, or that any defects will be corrected.`,
  },
  {
    h: "12. Limitation of Liability",
    p: `To the maximum extent permitted by law, Fount Stories will not be liable for any indirect, incidental, special, consequential, or punitive damages, or any loss of profits, revenue, data, or goodwill, arising from your use of the service. Our total aggregate liability for any claim relating to the service will not exceed the greater of (a) the amount you paid us in the twelve (12) months before the claim, or (b) one hundred US dollars (USD $100).`,
  },
  {
    h: "13. Changes to These Terms",
    p: `We may update these Terms from time to time. If a change is material, we will notify you by email or via the service at least seven (7) days before it takes effect. Continued use after the effective date constitutes acceptance of the updated Terms.`,
  },
  {
    h: "14. Governing Law and Disputes",
    p: `These Terms are governed by the laws of the State of Delaware, United States, without regard to its conflict of laws principles. Any dispute that cannot be resolved informally will be resolved in the state or federal courts located in Delaware, and you consent to personal jurisdiction there.`,
  },
  {
    h: "15. Contact",
    p: `Questions about these Terms? Email us at brief@fountstories.com.`,
  },
];

export default function Terms() {
  return (
    <LegalShell title="Terms of Service" lastUpdated="June 26, 2026">
      <p className="text-sm text-gray-600 leading-relaxed">
        Welcome to Fount Stories. These Terms govern your use of our website and services. Please read them carefully — they include important provisions about subscriptions, AI-generated content, and limitations of liability.
      </p>
      <div className="mt-10 space-y-9">
        {SECTIONS.map((s) => (
          <section key={s.h}>
            <h2 className="font-display text-2xl tracking-[-0.02em] font-medium leading-tight">{s.h}</h2>
            <p className="text-[15px] text-gray-700 leading-relaxed mt-3">{s.p}</p>
          </section>
        ))}
      </div>
    </LegalShell>
  );
}

export function LegalShell({ title, lastUpdated, children }) {
  return (
    <div className="min-h-screen bg-newsprint">
      <div className="border-b border-[var(--border)] bg-white/70 backdrop-blur-sm">
        <div className="max-w-5xl mx-auto px-6 lg:px-10 h-9 flex items-center justify-between text-[10px] font-mono uppercase tracking-[0.25em] text-gray-600">
          <Link to="/" className="hover:text-black flex items-center gap-2">
            <span className="brand-mark"><span /><span /><span /></span>
            <span className="font-display text-[14px] font-semibold tracking-[-0.02em]">Fount Stories</span>
          </Link>
          <span>Last updated · {lastUpdated}</span>
        </div>
      </div>

      <article className="max-w-3xl mx-auto px-6 lg:px-10 pt-16 pb-24">
        <div className="font-mono text-xs uppercase tracking-[0.3em] text-gray-500 mb-4">Legal</div>
        <h1 className="font-display text-5xl lg:text-6xl tracking-[-0.03em] font-medium leading-[0.95]">
          {title}
        </h1>
        <div className="mt-10">{children}</div>

        <div className="mt-16 pt-8 border-t border-[var(--border)] flex items-center justify-between text-[11px] font-mono uppercase tracking-widest text-gray-600">
          <Link to="/" className="hover:text-black">← Back to Fount</Link>
          <div className="flex items-center gap-6">
            <Link to="/support" className="hover:text-black">Support</Link>
            <Link to="/refund-policy" className="hover:text-black">Refunds</Link>
            <Link to="/terms" className="hover:text-black">Terms</Link>
            <Link to="/privacy" className="hover:text-black">Privacy</Link>
          </div>
        </div>
      </article>
    </div>
  );
}
